// Finding an online war (docs/ARCHITECTURE.md §15): a quick match against whoever is searching on the same continent,
// or a friend's challenge — the challenger shares a link carrying an invite code, and whoever opens it meets them.
// Only modes with an online queue can be played online; the live site offers released modes only (ROADMAP D30).

import type { CountryCode, GameModeKey } from '@taxwar/content'
import {
  CLOUDFLARE_DUEL_QUEUE,
  COUNTRY_PARAM,
  INVITE_CODE_PATTERN,
  INVITE_PARAM,
  OnlineInvariantError,
  QUEUE_PARAM,
  newInviteCode,
  type MatchAssignment,
  type SearchPhase,
} from '@taxwar/online'
import type { EdgeOrigin } from './backend'
import { openCloudflareTicket } from './cloudflare-ticket'

/** Modes that have an online queue. */
const ONLINE_QUEUES: Readonly<Partial<Record<GameModeKey, string>>> = {
  duel: CLOUDFLARE_DUEL_QUEUE,
}

export function playableOnline(mode: GameModeKey): boolean {
  return ONLINE_QUEUES[mode] !== undefined
}

/** How an online war is looked for. */
export type OnlineEntry =
  | { readonly kind: 'quick' }
  /** A new challenge: the player waits for a friend to open the link carrying `code`. */
  | { readonly kind: 'challenge'; readonly code: string }
  /** Accepting a friend's challenge link. */
  | { readonly kind: 'accept'; readonly code: string }

export interface OnlineWarRequest {
  readonly mode: GameModeKey
  /** The nation the player asks to lead; if the rival asked first for the same one, the room gives this player another. */
  readonly country: CountryCode
  readonly entry: OnlineEntry
}

/** A new challenge for the player to share. */
export function newChallenge(): OnlineEntry {
  return { kind: 'challenge', code: newInviteCode() }
}

const CHALLENGE_PARAM = 'challenge'

/** The link a challenger shares: the game, opened on the challenge. */
export function challengeLink(origin: string, code: string): string {
  return `${origin}/?${new URLSearchParams({ [CHALLENGE_PARAM]: code }).toString()}`
}

/** The challenge a page was opened with (a friend's link), if any; malformed codes are ignored. */
export function challengeCodeIn(search: string): string | null {
  const code = new URLSearchParams(search).get(CHALLENGE_PARAM)
  return code !== null && INVITE_CODE_PATTERN.test(code) ? code : null
}

export function findWarMatch(edge: EdgeOrigin, request: OnlineWarRequest, onPhase: (phase: SearchPhase) => void, signal: AbortSignal): Promise<MatchAssignment> {
  const queue = ONLINE_QUEUES[request.mode]
  if (queue === undefined) throw new OnlineInvariantError(`mode ${request.mode} has no online queue`)
  const params = new URLSearchParams({ [QUEUE_PARAM]: queue, [COUNTRY_PARAM]: request.country })
  if (request.entry.kind !== 'quick') params.set(INVITE_PARAM, request.entry.code)
  return openCloudflareTicket(edge, params, onPhase, signal)
}
